import { Box, Button, Stack, Typography } from '@mui/material'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { LinkUnstyled, unit } from '@hcr/ui'
import { isNotNull, isNotUndefined } from '@hcr/utils'

import { useHoliday } from '../../../../contexts'
import {
  useAccommodationDetailsQuery,
  useDestinationDetailsQuery,
  useIdToken,
  useRoomDetailsQuery,
  useTicketsCount,
} from '../../../../hooks'
import { Path } from '../../../../models'
import { to, createAccommodationTicketsTotalCount } from '../../../../utils'
import { TicketsInfo } from '../../tickets-info'
import { BookingDates, LayoutBookingOverview, LayoutBookingOverviewContent } from '../common'

import { BookingOverviewCompactLoading } from './BookingOverviewCompactLoading'

export const BookingOverviewCompact: FC = () => {
  const { t } = useTranslation()
  const idToken = useIdToken()
  const holiday = useHoliday()

  const accommodation = useAccommodationDetailsQuery(
    { idToken: String(idToken), accommodationId: Number(holiday.data.accommodationId) },
    { enabled: isNotNull(holiday.data.accommodationId) && isNotNull(idToken) }
  )

  const destinationDetails = useDestinationDetailsQuery(
    { destinationId: String(holiday.data.destinationId) },
    { enabled: isNotNull(holiday.data.destinationId) }
  )

  const roomDetails = useRoomDetailsQuery(
    { destinationId: String(holiday.data.destinationId), roomId: String(accommodation.data?.room_type_id) },
    {
      enabled:
        isNotNull(holiday.data.destinationId) &&
        accommodation.isSuccess &&
        isNotUndefined(accommodation.data?.room_type_id),
    }
  )

  const ticketsCount = useTicketsCount()

  if (holiday.isSuccess && accommodation.isSuccess && isNotUndefined(destinationDetails.data)) {
    const totalTickets = createAccommodationTicketsTotalCount(ticketsCount, accommodation.data)

    return (
      <LayoutBookingOverview>
        <Stack paddingBottom={unit(5)} paddingTop={unit(2)} justifyItems='center' alignItems='center'>
          <Typography visibility='hidden' component='h1' sx={{ height: unit(0) }} fontWeight='initial'>
            {destinationDetails.data.name}
          </Typography>

          <Box
            component='img'
            alt={destinationDetails.data.name}
            src={destinationDetails.data.logoUrl}
            width='auto'
            height={unit(9)}
          />
        </Stack>

        <LayoutBookingOverviewContent>
          <BookingDates
            checkInDate={accommodation.data.start_date}
            checkInHour={roomDetails.data?.checkInHour}
            checkOutDate={accommodation.data.end_date}
            checkOutHour={roomDetails.data?.checkOutHour}
          />

          {isNotNull(ticketsCount) && totalTickets > 0 && (
            <Box paddingBottom={unit(5)}>
              <TicketsInfo totalTickets={totalTickets} />
            </Box>
          )}

          <Button component={LinkUnstyled} to={to(Path.Booking)} variant='outlined' color='white'>
            {t('booking-overview.booking-and-program')}
          </Button>
        </LayoutBookingOverviewContent>
      </LayoutBookingOverview>
    )
  }

  return <BookingOverviewCompactLoading />
}
